import type { NdjsonChunkType, SimulationProposalType } from '@/types/StreamingTypes';

const VALID_CHUNK_TYPES = new Set(['message', 'reasoning']);

/**
 * Parses a single NDJSON line from the stream.
 * Each line is a complete JSON object with type, content, node, text, reasoning.
 * Returns null for empty lines or unparseable content.
 */
export const parseNdjsonLine = (line: string): NdjsonChunkType | null => {
    const trimmed = line.trim();

    if (!trimmed) {
        return null;
    }

    try {
        const parsed: unknown = JSON.parse(trimmed);

        if (typeof parsed !== 'object' || parsed === null) {
            return null;
        }

        const obj = parsed as Record<string, unknown>;

        if (typeof obj.type !== 'string' || !VALID_CHUNK_TYPES.has(obj.type)) {
            return null;
        }

        return {
            type: obj.type as 'message' | 'reasoning',
            content: typeof obj.content === 'string' ? obj.content : '',
            node: typeof obj.node === 'string' ? obj.node : '',
            text: typeof obj.text === 'string' ? obj.text : '',
            reasoning: typeof obj.reasoning === 'string' ? obj.reasoning : '',
        };
    } catch {
        return null;
    }
};

const isSimulationProposal = (item: unknown): item is SimulationProposalType => {
    if (typeof item !== 'object' || item === null) {
        return false;
    }

    const obj = item as Record<string, unknown>;

    return (
        typeof obj.id === 'string' &&
        typeof obj.title === 'string' &&
        typeof obj.description === 'string' &&
        typeof obj.params === 'object' &&
        obj.params !== null
    );
};

export type StreamingErrorType = {
    content: string;
    statusCode?: number;
};

/**
 * Parses a server-rendered error event. The backend emits `{type: 'error', content, status_code, ...}`
 * when the upstream AI Chat API fails (e.g. HTTP 429 daily limit).
 * Returns null for any other event type.
 */
export const parseErrorChunk = (line: string): StreamingErrorType | null => {
    const trimmed = line.trim();

    if (!trimmed) {
        return null;
    }

    try {
        const parsed: unknown = JSON.parse(trimmed);

        if (typeof parsed !== 'object' || parsed === null) {
            return null;
        }

        const obj = parsed as Record<string, unknown>;

        if (obj.type !== 'error') {
            return null;
        }

        const content = typeof obj.content === 'string' ? obj.content : '';
        const statusCode = typeof obj.status_code === 'number' ? obj.status_code : undefined;

        if (!content) {
            return null;
        }

        return { content, statusCode };
    } catch {
        return null;
    }
};

/**
 * Parses a complete event chunk and extracts simulation_proposals if present.
 */
export const parseCompleteChunk = (line: string): SimulationProposalType[] => {
    const trimmed = line.trim();

    if (!trimmed) {
        return [];
    }

    try {
        const parsed: unknown = JSON.parse(trimmed);

        if (typeof parsed !== 'object' || parsed === null) {
            return [];
        }

        const obj = parsed as Record<string, unknown>;

        if (obj.type !== 'complete') {
            return [];
        }

        if (!Array.isArray(obj.simulation_proposals)) {
            return [];
        }

        return obj.simulation_proposals.filter(isSimulationProposal);
    } catch {
        return [];
    }
};

/**
 * Extracts the conversation title from a `type: 'complete'` chunk if the server
 * provided one. Prefers `conversation_title`, falls back to the legacy
 * `conversation_name`. Returns null if the chunk is not a complete event,
 * no title field is present, or the value is not a non-empty string.
 */
export const parseCompleteTitle = (line: string): string | null => {
    const trimmed = line.trim();

    if (!trimmed) {
        return null;
    }

    try {
        const parsed: unknown = JSON.parse(trimmed);

        if (typeof parsed !== 'object' || parsed === null) {
            return null;
        }

        const obj = parsed as Record<string, unknown>;

        if (obj.type !== 'complete') {
            return null;
        }

        for (const key of ['conversation_title', 'conversation_name'] as const) {
            const value = obj[key];
            if (typeof value === 'string' && value !== '') {
                return value;
            }
        }

        return null;
    } catch {
        return null;
    }
};
